<template>
  <div class="appointment-detail-page">
    
    <div class="container">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>Cargando información de la cita...</p>
      </div>
      
      <div v-else-if="appointment" class="appointment-card">
        <div class="appointment-header">
          <div class="appointment-icon">
            <i class="fa-solid fa-calendar-check"></i>
          </div>
          <div class="appointment-info">
            <h1 class="appointment-title">Cita Médica #{{ appointment.id }}</h1>
            <p class="appointment-date">{{ formatDate(appointment.day) }} - {{ appointment.hour }}</p>
            <div class="appointment-status" :class="getStatusClass(appointment)">
              <span class="status-badge">{{ getStatusText(appointment) }}</span>
            </div>
          </div>
        </div>

        <div class="patient-section">
          <h2>Información del Paciente</h2>
          <div class="patient-info">
            <div class="patient-card">
              <h3>Datos Personales</h3>
              <p><strong>Nombre:</strong> {{ patient.name }} {{ patient.lastname_f }} {{ patient.lastname_m }}</p>
              <p><strong>Fecha de nacimiento:</strong> {{ formatDate(patient.date_birth) }}</p>
              <p><strong>Edad:</strong> {{ patient.age }} años</p>
              <p><strong>Género:</strong> {{ patient.gender }}</p>
              <p><strong>Teléfono:</strong> {{ patient.phone }}</p>
              <p><strong>Email:</strong> {{ patient.email }}</p>
            </div>
          </div>
        </div>

        <div class="doctor-section">
          <h2>Información del Médico</h2>
          <div class="doctor-info">
            <div class="doctor-card">
              <h3>Dr. {{ doctor.name }} {{ doctor.lastname }}</h3>
              <p><strong>Especialidad:</strong> {{ doctor.specialty }}</p>
              <p><strong>Cédula:</strong> {{ doctor.number_id }}</p>
              <p><strong>Teléfono:</strong> {{ doctor.phone }}</p>
            </div>
          </div>
        </div>

        <div v-if="appointment.reason" class="reason-section">
          <h2>Motivo de la Consulta</h2>
          <div class="reason-content">
            <p>{{ appointment.reason }}</p>
          </div>
        </div>

        <div v-if="appointment.diagnostic_print" class="diagnostic-section">
          <h2>Impresión Diagnóstica</h2>
          <div class="diagnostic-content">
            <p>{{ appointment.diagnostic_print }}</p>
          </div>
        </div>

        <div v-if="appointment.notes" class="notes-section">
          <h2>Notas de la Consulta</h2>
          <div class="notes-content">
            <p>{{ appointment.notes }}</p>
          </div>
        </div>

        <div v-if="vitalsigns && vitalsigns.length > 0" class="vitalsigns-section">
          <h2>Signos Vitales</h2>
          <div class="vitalsigns-grid">
            <div 
              v-for="vital in vitalsigns" 
              :key="vital.id"
              class="vital-card"
            >
              <div class="vital-header">
                <h4>Registro del {{ formatDate(vital.date) }}</h4>
              </div>
              <div class="vital-details">
                <div class="vital-item">
                  <i class="fa-solid fa-ruler-vertical"></i>
                  <strong>Estatura:</strong> {{ vital.stature }} cm
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-weight-hanging"></i>
                  <strong>Peso:</strong> {{ vital.weight }} kg
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-calculator"></i>
                  <strong>IMC:</strong> {{ vital.imc }} kg/m²
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-heart"></i>
                  <strong>F.C.:</strong> {{ vital.fc }} lpm
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-lungs"></i>
                  <strong>F.R.:</strong> {{ vital.fr }} rpm
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-thermometer-half"></i>
                  <strong>Temp.:</strong> {{ vital.temperature }} °C
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-tint"></i>
                  <strong>P.A.:</strong> {{ vital.pa }} mmHg
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-wind"></i>
                  <strong>SpO2:</strong> {{ vital.sp02 }} %
                </div>
                <div class="vital-item">
                  <i class="fa-solid fa-syringe"></i>
                  <strong>Glucosa:</strong> {{ vital.glucose }} mg/dl
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="prescriptions && prescriptions.length > 0" class="prescriptions-section">
          <h2>Recetas Médicas</h2>
          <div class="prescriptions-grid">
            <div 
              v-for="prescription in prescriptions" 
              :key="prescription.id"
              class="prescription-card"
            >
              <div class="prescription-header">
                <h4>Receta del {{ formatDate(prescription.date) }}</h4>
              </div>
              <div class="prescription-details">
                <p><strong>Diagnóstico:</strong> {{ prescription.diagnostic_print }}</p>
                <p><strong>Comentarios:</strong> {{ prescription.comments }}</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="exams && exams.length > 0" class="exams-section">
          <h2>Exámenes Solicitados</h2>
          <div class="exams-grid">
            <div 
              v-for="exam in exams" 
              :key="exam.id"
              class="exam-card"
            >
              <div class="exam-header">
                <h4>{{ exam.name }}</h4>
                <span class="exam-type">{{ exam.type }}</span>
              </div>
              <div class="exam-details">
                <p><strong>Área:</strong> {{ exam.area }}</p>
                <p><strong>Diagnóstico:</strong> {{ exam.diagnostic }}</p>
                <p><strong>Comentarios:</strong> {{ exam.comments }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="error-message">
        <i class="fa-solid fa-exclamation-triangle"></i>
        <h2>Cita no encontrada</h2>
        <p>La cita que buscas no existe o ha sido eliminada.</p>
        <button @click="$router.push('/')" class="btn-home">
          <i class="fa-solid fa-home"></i>
          Volver al inicio
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppointmentDetail',
  data() {
    return {
      appointment: null,
      patient: null,
      doctor: null,
      vitalsigns: [],
      prescriptions: [],
      exams: [],
      loading: true,
      error: null
    }
  },
  methods: {
    async getAppointmentData() {
      try {
        this.loading = true;
        const appointmentId = this.$route.params.id;
        
        // Hacer petición para obtener datos de la cita
        const response = await axios.get(`/api/appointments/${appointmentId}/detail`);
        
        if (response.data) {
          this.appointment = response.data.appointment;
          this.patient = response.data.patient;
          this.doctor = response.data.doctor;
          this.vitalsigns = response.data.vitalsigns || [];
          this.prescriptions = response.data.prescriptions || [];
          this.exams = response.data.exams || [];
        }
      } catch (error) {
        console.error('Error fetching appointment data:', error);
        this.error = 'No se pudo cargar la información de la cita';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'No especificada';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getStatusClass(appointment) {
      const today = new Date();
      const appointmentDate = new Date(appointment.day);
      
      if (appointmentDate < today) {
        return 'status-completed';
      } else if (appointmentDate.toDateString() === today.toDateString()) {
        return 'status-today';
      } else {
        return 'status-upcoming';
      }
    },
    getStatusText(appointment) {
      const today = new Date();
      const appointmentDate = new Date(appointment.day);
      
      if (appointmentDate < today) {
        return 'Completada';
      } else if (appointmentDate.toDateString() === today.toDateString()) {
        return 'Hoy';
      } else {
        return 'Próxima';
      }
    }
  },
  mounted() {
    this.getAppointmentData();
  },
  watch: {
    '$route.params.id'() {
      this.getAppointmentData();
    }
  }
}
</script>

<style scoped>
.appointment-detail-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
}



.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem 2rem 1rem;
}

.loading {
  text-align: center;
  color: white;
  padding: 4rem 0;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid white;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.appointment-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(30, 64, 175, 0.15);
  overflow: hidden;
  margin-bottom: 2rem;
  border: 1px solid rgba(30, 64, 175, 0.1);
}

.appointment-header {
  display: flex;
  padding: 2rem;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 4px 20px rgba(30, 64, 175, 0.3);
}

.appointment-icon {
  flex-shrink: 0;
  margin-right: 2rem;
  font-size: 4rem;
  opacity: 0.9;
}

.appointment-info {
  flex: 1;
}

.appointment-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.appointment-date {
  font-size: 1.2rem;
  margin: 0 0 1rem 0;
  opacity: 0.9;
}

.appointment-status {
  margin-top: 1rem;
}

.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-completed .status-badge {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-today .status-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.status-upcoming .status-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
}

.patient-section,
.doctor-section,
.reason-section,
.diagnostic-section,
.notes-section,
.vitalsigns-section,
.prescriptions-section,
.exams-section {
  padding: 2rem;
  border-bottom: 1px solid #e5e7eb;
}

.patient-section h2,
.doctor-section h2,
.reason-section h2,
.diagnostic-section h2,
.notes-section h2,
.vitalsigns-section h2,
.prescriptions-section h2,
.exams-section h2 {
  color: #374151;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.patient-info,
.doctor-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.patient-card,
.doctor-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  border-left: 4px solid #1e40af;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.patient-card:hover,
.doctor-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(30, 64, 175, 0.15);
}

.patient-card h3,
.doctor-card h3 {
  color: #1f2937;
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
}

.patient-card p,
.doctor-card p {
  margin: 0.5rem 0;
  color: #4b5563;
}

.reason-content,
.diagnostic-content,
.notes-content {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  border-left: 4px solid #059669;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.reason-content:hover,
.diagnostic-content:hover,
.notes-content:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(5, 150, 105, 0.15);
}

.reason-content p,
.diagnostic-content p,
.notes-content p {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
}

.vitalsigns-grid,
.prescriptions-grid,
.exams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.vital-card,
.prescription-card,
.exam-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  border-left: 4px solid #dc2626;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.vital-card:hover,
.prescription-card:hover,
.exam-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(220, 38, 38, 0.15);
}

.vital-header,
.prescription-header,
.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.vital-header h4,
.prescription-header h4,
.exam-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.exam-type {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(30, 64, 175, 0.2);
}

.vital-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.vital-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.vital-item i {
  width: 16px;
  color: #6b7280;
}

.prescription-details p,
.exam-details p {
  margin: 0.5rem 0;
  color: #4b5563;
  font-size: 0.9rem;
}

.error-message {
  text-align: center;
  color: white;
  padding: 4rem 0;
}

.error-message i {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #fbbf24;
}

.error-message h2 {
  margin: 1rem 0;
  font-size: 2rem;
}

.error-message p {
  margin-bottom: 2rem;
  font-size: 1.1rem;
  opacity: 0.9;
}

.btn-home {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
}

.btn-home:hover {
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(30, 64, 175, 0.4);
}

@media (max-width: 768px) {
  .simple-header {
    padding: 0.75rem 0;
  }
  
  .logo-image {
    width: 32px;
    height: 32px;
  }
  
  .logo-text {
    font-size: 1.25rem;
  }
  
  .appointment-header {
    flex-direction: column;
    text-align: center;
  }
  
  .appointment-icon {
    margin-right: 0;
    margin-bottom: 1.5rem;
    font-size: 3rem;
  }
  
  .appointment-title {
    font-size: 2rem;
  }
  
  .patient-info,
  .doctor-info {
    grid-template-columns: 1fr;
  }
  
  .vitalsigns-grid,
  .prescriptions-grid,
  .exams-grid {
    grid-template-columns: 1fr;
  }
  
  .vital-details {
    grid-template-columns: 1fr;
  }
}
</style> 